import React, { useEffect } from 'react';
import { FormGroup, Grid, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectIsLoggedIn } from 'renderer/features/auth/state/userSelectors';
import { Field, useFormikContext } from 'formik';
import { CheckboxWithLabel, TextField as TextInput } from 'formik-mui';
import { AccountForm as AccountFormType } from 'renderer/features/purchase-page/schemas/purchase.schemas';
import { useParams } from 'react-router-dom';
import { useGetProductDetailsQuery } from 'renderer/features/products-page/services/products.endpoints';

interface AccountFormProps {
  disabled: boolean;
}

const AccountForm: React.FC<AccountFormProps> = ({ disabled }) => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const { id: productId } = useParams();
  const { data: productDetails } = useGetProductDetailsQuery(productId ?? '');
  const { values, setFieldValue } = useFormikContext<AccountFormType>();

  const isSubscription = productDetails?.category === 'Subscription';

  useEffect(() => {
    if (isSubscription && !isLoggedIn) {
      setFieldValue('createAccount', true);
      setFieldValue('savePayment', true);
    }
  }, [isLoggedIn, isSubscription, setFieldValue]);

  // Determine if the "createAccount" checkbox should be disabled
  const disableCreateAccount = isSubscription && !isLoggedIn;

  return (
    <>
      {!isLoggedIn && (
        <Typography variant="h6" sx={{ my: 2 }}>
          Contact
        </Typography>
      )}
      <Grid container spacing={2}>
        {!isLoggedIn && (
          <>
            <Grid item xs={12} sm={6}>
              <Field
                component={TextInput}
                disabled={disabled}
                name="firstName"
                label="First Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                component={TextInput}
                disabled={disabled}
                name="lastName"
                label="Last Name"
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                component={TextInput}
                disabled={disabled}
                name="email"
                label="Email"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <FormGroup row>
            {!isLoggedIn && (
              <Tooltip
                title={
                  disableCreateAccount
                    ? 'Creating an account is required to make a subscription purchases.'
                    : ''
                }
                arrow
              >
                <span>
                  <Field
                    component={CheckboxWithLabel}
                    disabled={disabled || disableCreateAccount}
                    type="checkbox"
                    name="createAccount"
                    Label={{ label: 'Create an account' }}
                  />
                </span>
              </Tooltip>
            )}
            {(isLoggedIn || values.createAccount) && (
              <Field
                component={CheckboxWithLabel}
                disabled={disabled}
                type="checkbox"
                name="savePayment"
                Label={{ label: 'Save this payment method' }}
              />
            )}
            {(isLoggedIn || values.createAccount) && values.savePayment && (
              <Field
                component={CheckboxWithLabel}
                disabled={disabled}
                type="checkbox"
                name="makeDefault"
                Label={{ label: 'Make this my default payment method' }}
              />
            )}
          </FormGroup>
        </Grid>
        {!isLoggedIn && values.createAccount && (
          <>
            <Grid item xs={12} sm={6}>
              <Field
                component={TextInput}
                disabled={disabled}
                name="password"
                label="Password"
                type="password"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                component={TextInput}
                disabled={disabled}
                name="confirmPassword"
                label="Confirm Password"
                type="password"
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default AccountForm;
